import React from 'react'
import { Box, Skeleton, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Card } from './ui'
import { color, dayLabel, type, usd } from './tokens'

function Label({ children, hint }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
      <Typography sx={type.label}>{children}</Typography>
      {hint && (
        <Tooltip title={hint} arrow>
          <InfoOutlinedIcon tabIndex={0} aria-label={hint} sx={{ fontSize: 15, color: color.faint, cursor: 'help', '&:focus-visible': { outline: `2px solid ${color.accentRing}`, borderRadius: '50%' } }} />
        </Tooltip>
      )}
    </Box>
  )
}

/** Seven daily totals as a line with a soft fill. Purely decorative for screen readers — the total is read out instead. */
function Sparkline({ values, width = 132, height = 40 }) {
  const max = Math.max(...values, 0)
  const step = values.length > 1 ? width / (values.length - 1) : width
  const y = (v) => (max > 0 ? height - 3 - ((height - 6) * v) / max : height - 3)
  const points = values.map((v, i) => `${(i * step).toFixed(1)},${y(v).toFixed(1)}`)
  const line = points.join(' ')
  const area = `0,${height} ${line} ${width},${height}`
  return (
    <Box component="svg" viewBox={`0 0 ${width} ${height}`} aria-hidden="true" sx={{ width, height, display: 'block', overflow: 'visible' }}>
      <defs>
        <linearGradient id="rmf-spark-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color.accent} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color.accent} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill="url(#rmf-spark-fill)" />
      <polyline points={line} fill="none" stroke={color.accent} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {values.length > 0 && (
        <circle cx={(values.length - 1) * step} cy={y(values[values.length - 1])} r="3" fill={color.surface} stroke={color.accent} strokeWidth="2" />
      )}
    </Box>
  )
}

function SkeletonCard() {
  return (
    <Card>
      <Skeleton width="45%" sx={{ bgcolor: '#EEF1F5' }} />
      <Skeleton width="65%" height={48} sx={{ bgcolor: '#EEF1F5' }} />
      <Skeleton width="50%" sx={{ bgcolor: '#EEF1F5' }} />
    </Card>
  )
}

/**
 * State B, top of page: what is ready to pay out, when the next payout lands,
 * and how the week has gone.
 */
export default function OverviewStrip({ overview, loading }) {
  const grid = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
    gap: { xs: 2, md: 2.5 },
  }

  if (loading && !overview) {
    return (
      <Box sx={grid} aria-busy="true">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </Box>
    )
  }
  if (!overview) return null

  const next = overview.nextPayout
  const week = overview.weekDaily || []

  return (
    <Box sx={grid}>
      <Card>
        <Label>Available balance</Label>
        <Typography sx={type.figure}>{usd(overview.availableBalance)}</Typography>
        <Typography sx={{ ...type.small, mt: 0.75 }}>
          Ready to pay out
          {overview.pendingBalance > 0 && (
            <Box component="span" sx={{ color: color.faint }}> · {usd(overview.pendingBalance)} on the way</Box>
          )}
        </Typography>
      </Card>

      <Card>
        <Label hint={next && next.estimated ? 'Estimated from your balance and payout schedule. Stripe confirms it on the day.' : null}>
          Next payout
        </Label>
        {next ? (
          <>
            <Typography sx={type.figure}>{usd(next.amount)}</Typography>
            <Typography sx={{ ...type.small, mt: 0.75 }}>
              {next.estimated ? 'Expected' : 'Arrives'} {dayLabel(next.date, { weekday: 'short', month: 'short', day: 'numeric' })}
              {next.bankLast4 && <Box component="span" sx={{ color: color.faint }}> · Bank ••{next.bankLast4}</Box>}
            </Typography>
          </>
        ) : (
          <>
            <Typography sx={{ ...type.figure, color: color.faint }}>—</Typography>
            <Typography sx={{ ...type.small, mt: 0.75 }}>
              {overview.payoutSchedule === 'Manual' ? 'Payouts are manual' : 'Nothing scheduled yet'}
            </Typography>
          </>
        )}
      </Card>

      <Card sx={{ gridColumn: { sm: 'span 2', lg: 'auto' } }}>
        <Label>This week</Label>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={type.figure}>{usd(overview.weekVolume)}</Typography>
            <Typography sx={{ ...type.small, mt: 0.75 }}>
              Your share from {overview.weekOrders} {overview.weekOrders === 1 ? 'order' : 'orders'} · last 7 days
            </Typography>
          </Box>
          {week.length > 1 && <Sparkline values={week} />}
        </Box>
      </Card>
    </Box>
  )
}
